/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import type { Unsubscribe } from '@amazon/vinyl-util'
import { combineData } from '@amazon/vinyl-observable'
import type { PlaylistEntry } from '@amzn/amp-playback'
import { entryTrackId } from '@/playback/playlistEntryIds'
import { nowPlaying$, nowPlayingPaused$ } from '@/blackbolt/playback'
import { setNowPlaying, type ViewContextNowPlaying } from '@/viewContext'

// Feeds the current-view-context provider's now-playing slice from the shared
// playback façade. Lives in `blackbolt/` (not `viewContext/`) so the pure
// `viewContext` leaf never imports `blackbolt/` — the injection direction is
// blackbolt → viewContext, one-way (blackbolt already depends downward on the
// provider). Wired once at boot; feeds globally so the agent knows the current
// track on ANY route, not just the now-playing screen.

/** Project a playing entry onto the thin now-playing context the agent reads. */
function toNowPlaying(entry: PlaylistEntry | null, paused: boolean): ViewContextNowPlaying | null {
    if (!entry) return null
    return {
        // Prefer the catalog track id (the actionable "add this …" target) and
        // fall back to the queue entry id when the feed doesn't carry one.
        id: entryTrackId(entry) ?? entry.id,
        title: entry.title,
        artist: entry.artistName,
        ...(entry.albumTitle ? { album: entry.albumTitle } : null),
        isPlaying: !paused,
        ...(entry.video ? { isVideo: true } : null),
    }
}

/** Subscribe the view-context provider to the current track. Call once at boot. */
export function startNowPlayingViewContextFeed(): Unsubscribe {
    return combineData({ entry: nowPlaying$, paused: nowPlayingPaused$ })
        .map(({ entry, paused }) => toNowPlaying(entry, paused))
        .onData(setNowPlaying)
}
